import LitJsSdk from "lit-js-sdk";
import { AccountId, ChainId } from "caip";
import { getChainFromAccountId } from "../../utils";
import { ApiServiceImpl } from "../core/api.service.impl";
import { createDIDKey } from "@glazed/did-session";
import { SiweMessage, SignatureType } from "lit-siwe";
import { toUtf8Bytes } from "@ethersproject/strings";
import { stores } from "../../stores/";
import { verifyMessage } from "@ethersproject/wallet";
import { ethers } from "ethers";
export class AuthServiceImpl extends ApiServiceImpl {
    constructor() {
        super(...arguments);
        this.litClient = new LitJsSdk.LitNodeClient();
    }
    async connect(signer) {
        try {
            await this.litClient.connect();
            const address = ethers.utils.getAddress(await signer.getAddress());
            const network = await signer.provider.getNetwork();
            const chainId = network.chainId;
            const accountId = new AccountId({
                chainId: {
                    namespace: "eip155",
                    reference: chainId.toString(),
                },
                address,
            });
            const didKey = await createDIDKey();
            const siweMessage = await this.generateSiweMessage(signer, accountId, didKey);
            const cacao = cacaoFromSiweMessage(siweMessage);
            verifyCacao(cacao);
            const didKeyWithCap = didKey.withCapability(cacao);
            await didKeyWithCap.authenticate();
            stores.authStore.authSession = {
                accessToken: await this.getLitToken(accountId, siweMessage),
                accountId: accountId,
                did: didKeyWithCap,
                cacao: cacao,
            };
            return this.success(stores.authStore.authSession);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async upsertUser(newAccountId) {
        try {
            if (!stores.authStore.authSession) {
                throw new Error("No authSession found");
            }
            const response = await this.post(`${this.urls.worker}/users/${newAccountId.toString()}`, undefined, {
                "x-lit-token": stores.authStore.authSession.accessToken,
            });
            return this.success(response.data["userId"]);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async generateSiweMessage(signer, accountId, sessionDID) {
        const preparedMessage = {
            domain: globalThis.location.host,
            address: accountId.address,
            uri: sessionDID.id,
            version: "1",
            chainId: Number(accountId.chainId.reference),
        };
        const message = new SiweMessage(preparedMessage);
        const body = message.prepareMessage();
        const messageBytes = toUtf8Bytes(body);
        const signature = await signer.signMessage(messageBytes);
        message.signature = signature;
        return message;
    }
    async getLitToken(accountId, siweMessage) {
        const litChain = getChainFromAccountId(accountId);
        const authSig = {
            sig: siweMessage.signature,
            derivedVia: "web3.eth.personal.sign",
            signedMessage: siweMessage.prepareMessage(),
            address: siweMessage.address,
        };
        const accessControlConditions = this.generateAccessControlCondition(accountId, authSig.address);
        const resourceId = this.generateResource(accountId);
        let litToken;
        try {
            litToken = await this.litClient.getSignedToken({
                accessControlConditions,
                chain: litChain,
                authSig,
                resourceId,
            });
        }
        catch (e) {
            await this.litClient.saveSigningCondition({
                accessControlConditions,
                chain: litChain,
                authSig,
                resourceId,
            });
            litToken = await this.litClient.getSignedToken({
                accessControlConditions,
                chain: litChain,
                authSig,
                resourceId,
            });
        }
        return litToken;
    }
    generateAccessControlCondition(accountId, account) {
        return [
            {
                contractAddress: "",
                standardContractType: "",
                chain: getChainFromAccountId(accountId),
                method: "",
                parameters: [":userAddress"],
                returnValueTest: {
                    comparator: "=",
                    value: account.toLowerCase(),
                },
            },
        ];
    }
    generateResource(accountId) {
        return {
            baseUrl: "stream.hashchat.xyz",
            path: `/token/${accountId}`,
            orgId: "",
            role: "",
            extraData: "",
        };
    }
    clearKeys() {
        localStorage.clear();
    }
}
function cacaoFromSiweMessage(siweMessage) {
    const cacao = {
        h: {
            t: "eip4361",
        },
        p: {
            domain: siweMessage.domain,
            iat: siweMessage.issuedAt,
            iss: `did:pkh:eip155:${siweMessage.chainId}:${siweMessage.address}`,
            aud: siweMessage.uri,
            version: siweMessage.version,
            nonce: siweMessage.nonce,
        },
    };
    if (siweMessage.signature) {
        cacao.s = {
            t: "eip191",
            s: siweMessage.signature,
        };
    }
    if (siweMessage.notBefore) {
        cacao.p.nbf = siweMessage.notBefore;
    }
    if (siweMessage.expirationTime) {
        cacao.p.exp = siweMessage.expirationTime;
    }
    if (siweMessage.statement) {
        cacao.p.statement = siweMessage.statement;
    }
    if (siweMessage.requestId) {
        cacao.p.requestId = siweMessage.requestId;
    }
    if (siweMessage.resources) {
        cacao.p.resources = siweMessage.resources;
    }
    return cacao;
}
function siweFromCacao(cacao) {
    const account = AccountId.parse(cacao.p.iss.replace("did:pkh:", ""));
    const siwe = new SiweMessage({
        domain: cacao.p.domain,
        address: account.address,
        uri: cacao.p.aud,
        version: cacao.p.version,
        chainId: Number(new ChainId(account.chainId).reference),
    });
    if (cacao.p.statement)
        siwe.statement = cacao.p.statement;
    if (cacao.p.nonce)
        siwe.nonce = cacao.p.nonce;
    if (cacao.p.iat)
        siwe.issuedAt = cacao.p.iat;
    if (cacao.p.exp)
        siwe.expirationTime = cacao.p.exp;
    if (cacao.p.nbf)
        siwe.notBefore = cacao.p.nbf;
    if (cacao.p.requestId)
        siwe.requestId = cacao.p.requestId;
    if (cacao.p.resources)
        siwe.resources = cacao.p.resources;
    if (cacao.s) {
        if (cacao.s.s)
            siwe.signature = cacao.s.s;
        if (cacao.s.t === "eip191")
            siwe.type = SignatureType.PERSONAL_SIGNATURE;
    }
    return siwe;
}
export function verifyCacao(cacao, options = {}) {
    if (!cacao.s) {
        throw new Error(`CACAO does not have a signature`);
    }
    const atTime = options.atTime ? options.atTime.getTime() : Date.now();
    const clockSkew = (options.clockSkewSecs ?? 100) * 1000;
    if (Date.parse(cacao.p.iat) > atTime + clockSkew ||
        (cacao.p.nbf && Date.parse(cacao.p.nbf) > atTime + clockSkew)) {
        throw new Error(`CACAO is not valid yet`);
    }
    const phaseOutMS = options.revocationPhaseOutSecs
        ? options.revocationPhaseOutSecs * 1000
        : 0;
    if (cacao.p.exp &&
        Date.parse(cacao.p.exp) + phaseOutMS + clockSkew < atTime) {
        throw new Error(`CACAO has expired`);
    }
    const msg = siweFromCacao(cacao);
    const sig = cacao.s.s;
    const recoveredAddress = verifyMessage(msg.toMessage(), sig);
    const issAddress = AccountId.parse(cacao.p.iss.replace("did:pkh:", "")).address;
    if (recoveredAddress.toLowerCase() !== issAddress.toLowerCase()) {
        throw new Error(`Signature does not belong to issuer`);
    }
}
